import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import PasswordRequirements from '../components/PasswordRequirements';

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setStatus(null);
    setLoading(true);
    try {
      const response = await authService.resetPassword(token, newPassword, confirmPassword);
      setStatus(response.message);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6 py-16 text-white">
      <div className="max-w-lg w-full bg-white/10 border border-white/20 rounded-3xl p-8 backdrop-blur">
        <h1 className="text-2xl font-semibold mb-2">Reset your password</h1>
        <p className="text-sm text-white/70 mb-6">
          Enter a new password to regain access.
        </p>
        {!token && (
          <div className="text-sm text-red-300 mb-4">
            Invalid or missing reset token.
          </div>
        )}
        <form className="space-y-4" onSubmit={handleSubmit}>
          {status && <div className="text-sm text-emerald-200">{status}</div>}
          {error && <div className="text-sm text-red-300">{error}</div>}
          <div>
            <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
              New password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="••••••••"
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
              Confirm password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="••••••••"
              required
            />
          </div>
          <PasswordRequirements
            password={newPassword}
            confirm={confirmPassword}
            showConfirmMatch
            className="bg-white/5"
          />
          <button
            type="submit"
            disabled={loading || !token}
            className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold disabled:opacity-60"
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
        <div className="mt-6 text-sm text-white/70">
          <Link to="/login" className="text-emerald-200 hover:text-emerald-100">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
